import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { ContextService } from 'src/core/services/context.service';
import { ObjectUtils } from 'src/core/utils/object';
import { SupplierFormView } from './supplier-form.view';
import { SupplierDataService } from './supplier-form.data';
import { TransportCompanyOfficeListDataService } from '../../office/office.data';
import { EntitySchemaDataService } from 'src/app/general/entity-schemas/entity-schema.data';
import { IdentityService } from 'src/core/services/identity.service';
import { ZuluIcon } from 'src/app/shared/icon';

interface Props extends ControllerProps<{ id: string; }> {
}

interface State {
	mode: ControlMode;
	schema?: JsonSchema;
	refresh: boolean;
}

@observer
export class SupplierFormController extends React.Component<Props, State> {
	@resolve
	contextService: ContextService;

	@resolve
	identity: IdentityService;

	@resolve
	toast: ToastNotifications;

	@observable
	breadcrumb: any[] = [];

	@observable
	dataService: SupplierDataService;

	@observable
	officeDataService: TransportCompanyOfficeListDataService;

	constructor(props: Readonly<Props>) {
		super(props);
		this.state = {
			mode: 'display',
			refresh: false
		}
		
	}

	componentDidMount() {
		this.props.setBusy(true);
		Promise.all([this.loadData(), this.loadSchema()])
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private async loadData(): Promise<void> {
		this.dataService = SupplierDataService.make(this.contextService);
		this.officeDataService = TransportCompanyOfficeListDataService.make(this.contextService);

		if (this.props.match.params.id == 'General' || this.props.match.params.id == 'CarRent') {
			// Create new
			await this.dataService.add(this.props.match.params.id, this.identity.userId);
			this.handleModeChange('edit');
		} else {
			// Load entity
			this.dataService.addFilter('id', 'eq', this.props.match.params.id);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;
			await this.dataService.load(query => query);

			const supplier = this.dataService.item;
			if (Object.getOwnPropertyNames(supplier.dynamicProperties).find(x => x == 'language') == null) {
				supplier.dynamicProperties['language'] = null;
			}
		}
	}

	private async loadSchema(): Promise<void> {
		const entitySchemaService = EntitySchemaDataService.make();

		entitySchemaService.addFilter('name', 'eq', 'Supplier');
		entitySchemaService.addSorting('name', 'asc', true);
		entitySchemaService.pageSize = 1;

		const entitySchemas = await entitySchemaService.load(query => query);
		const schema = ObjectUtils.toObject<JsonSchema>(entitySchemas[0].schema);
		this.setState({ schema: schema });
	}

	@computed
	get title() {
		if (this.dataService && this.dataService.item) {
			return `${this.dataService.item.type == 0 ? 'Supplier' : 'Transport company'} ${this.dataService.item.title || ''}`;
		}
		return '';
	}

	private get isNew(): boolean {
		return this.props.match.params.id == 'General' || this.props.match.params.id == 'CarRent';
	}

	goToCard = async (text: any, key: string, model: Model, parentModel: Model): Promise<void> => {
		const errors = await parentModel.validate(false);
		if (errors.length == 0) {
			if (this.breadcrumb.length == 0) {
				let textView = <React.Fragment>
					<span>Supplier</span>
					<ZuluIcon type='close' />
					<br></br>
					<span>{(this.dataService.item.title) ? this.dataService.item.title : 'New Supplier'}</span>
				</React.Fragment>
				this.breadcrumb.push({
					text: textView,
					key: 'supplier',
					onClick: () => { this.closeItem(key, model) },
					breadcrumbName: (this.dataService.item.title) ? this.dataService.item.title : 'New Supplier',
					item: this.dataService.item

				});

			}

			this.breadcrumb.push({
				text: text,
				breadcrumbName: text,
				key: key,
				onClick: () => { this.closeItem(key, model) },
				item: model

			});
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	};

	closeItem = async (key: string, model?: Model): Promise<void> => {
		const errors = await model.validate(false);
		if (errors.length == 0) {
			let index = this.breadcrumb.findIndex(i => i.key == key);
			if (index != -1) {
				index = index == 1 ? 0 : index;
				this.breadcrumb.splice(index, this.breadcrumb.length - index);
			}
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	};

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode: mode });
	};

	validateOffice = async (): Promise<ValidationError[]> => {
		let res = [];
		for (let office of this.officeDataService.items) {
			const errors = await office.validate(true);
			res = res.concat(errors)
		}

		return res;
	}

	handleSave = async (): Promise<void> => {
		const item = this.dataService.item;
		let errors = await item.validate(true);
		const officeError = await this.validateOffice();
		errors = errors.concat(officeError);
		if (errors.length == 0) {
			this.props.setBusy(true);
			this.dataService.save().then(n => {
				this.props.setBusy(false);
				this.toast.showDataSaved();
				this.props.history.replace(`/products/suppliers`);
			})
				.catch(error => {
					this.props.setBusy(false);
					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
				});
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	};

	handleCancel = (): void => {
		this.props.history.replace(`/products/suppliers`);
	};
	@autobind
	private onLogoChange(ev: React.ChangeEvent<HTMLInputElement>) {
		const file = ev.target.files[0];
		if (file) {
			const contentReader = new FileReader();
			contentReader.onload = (e: Event) => {
				const result: ArrayBuffer = (e.target as any).result;
				this.dataService.item.logo = new Uint8Array(result);
				this.setState({
					refresh: !this.state.refresh
				});
			};
			contentReader.readAsArrayBuffer(file);
		}
	}
	render() {
		if (this.dataService == null || this.state.schema == null) {
			return null;
		}

		return (
			<SupplierFormView
				breadcrumb={this.breadcrumb.slice()}
				goToCard={this.goToCard}
				model={this.dataService.item}
				title={this.title}
				mode={this.state.mode}
				onModeChanged={this.handleModeChange}
				cancel={this.handleCancel}
				save={this.handleSave}
				contextService={this.contextService}
				setBusy={this.props.setBusy}
				officeDataService={this.officeDataService}
				isNew={this.isNew}
				schema={this.state.schema}
				onLogoChange={this.onLogoChange}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/products/suppliers/form/supplier-form.controller.tsx